import React, { useEffect, useState } from "react";
import wallet from "../../images/Icons/wallet.png";
import axios from "axios";
import config from "../../config";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router";
import useLogout from "../Auth/useLogout";
import Dataloader from "../../Component/Dataloader";
import NoData from "../../Component/NoData";

export default function CreditRequest() {
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const { id } = useParams();
  const [isEditMode, setIsEditMode] = useState(false);
  const [requestId, setRequestId] = useState(null);

  useEffect(() => {
    if (!id && (location.state === null || location.state.assignedAmount > 0)) {
      navigate("/");
    }
    if (id) {
      setIsEditMode(true);
      setRequestId(id);
    }
  }, [location.state, navigate, id]);

  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [formData, setFormData] = useState({});
  const [fileUrls, setFileUrls] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [notes, setNotes] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  useEffect(() => {
    const fetchDocuments = async () => {
      setDataLoading(true);
      try {
        const response = await axios.get(
          `${config.apiUrl}/credit-wallet-document-type`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: config.token,
              Accept: "application/json",
            },
          }
        );
        setDocuments(response.data.data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          logout();
        }
      } finally {
        setDataLoading(false);
      }
    };

    fetchDocuments();
  }, [logout]);

  // Fetch existing request data when in edit mode
  useEffect(() => {
    const fetchRequestData = async () => {
      if (!isEditMode || !requestId) return;
      
      setDataLoading(true);
      try {
        const response = await axios.get(
          `${config.apiUrl}/credit-wallet-request/${requestId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: config.token,
              Accept: "application/json",
            },
          }
        );
        
        const requestData = response.data.data;
        // Find and set the selected document
        const docType = documents.find(doc => doc.id === requestData.credit_wallet_document_type_id);
        if (docType) {
          setSelectedDocument(docType);
          
          // Pre-fill form data with existing values
          const prefilledData = {};
          const fileUrlData = {};
          requestData.form_data.forEach(field => {
            // Trim label to handle any whitespace differences
            const trimmedLabel = field.label.trim();
            prefilledData[trimmedLabel] = field.value;
            if (field.file_url) {
              fileUrlData[trimmedLabel] = field.file_url;
            }
          });
          setFormData(prefilledData);
          setFileUrls(fileUrlData);
          setNotes(requestData.notes || "");
          setDescription(requestData.description || "");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to fetch request data", {
          position: "top-right",
          autoClose: 2000,
        });
        if (error.response && error.response.status === 401) {
          logout();
        }
      } finally {
        setDataLoading(false);
      }
    };

    if (documents.length > 0) {
      fetchRequestData();
    }
  }, [isEditMode, requestId, documents, logout]);

  const handleDocumentSelect = (e) => {
    const documentId = e.target.value;
    if (documentId) {
      const selected = documents.find(doc => doc.id === parseInt(documentId));
      setSelectedDocument(selected);
      setFormData({});
      setUploadingFiles({});
    } else {
      setSelectedDocument(null);
      setFormData({});
      setUploadingFiles({});
    }
  };

  const handleInputChange = (label, value) => {
    setFormData(prev => ({
      ...prev,
      [label]: value
    }));
  };

  const handleFileChange = async (label, file) => {
    if (!file) return;

    setUploadingFiles(prev => ({ ...prev, [label]: true }));

    try {
      const fileData = new FormData();
      fileData.append('image', file);
      fileData.append('purpose', 'credit_wallet_request');

      const response = await axios.post(
        `${config.apiUrl}/image-upload`,
        fileData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: config.token,
            Accept: "application/json",
          },
        }
      );

      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          [label]: response.data.id
        }));
        toast.success(response.data.message || "File uploaded successfully", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "File upload failed", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      if (error.response && error.response.status === 401) {
        logout();
      }
    } finally {
      setUploadingFiles(prev => ({ ...prev, [label]: false }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedDocument) {
      toast.error("Please select a document type", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    const requiredFields = selectedDocument.forms.filter(field => field.required);
    for (let field of requiredFields) {
      const trimmedLabel = field.label.trim();
      if (!formData[trimmedLabel]) {
        toast.error(`${field.label} is required`, {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        return;
      }
    }

    setLoading(true);
    try {
      const formsData = selectedDocument.forms.map(field => {
        const trimmedLabel = field.label.trim();
        return {
          label: field.label,
          required: field.required,
          type: field.type,
          description: field.description,
          value: formData[trimmedLabel] || ""
        };
      });

      const submitData = {
        credit_wallet_document_type_id: selectedDocument.id,
        forms: formsData
      };
      
      const apiUrl = isEditMode 
        ? `${config.apiUrl}/credit-wallet-request/${requestId}`
        : `${config.apiUrl}/credit-wallet-request`;

      const response = await axios.post(
        apiUrl,
        submitData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: config.token,
            Accept: "application/json",
          },
        }
      );

      toast.success(response.data.message || (isEditMode ? "Request updated successfully" : "Request submitted successfully"), {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setTimeout(() => {
        setSelectedDocument(null);
        setFormData({});
        navigate("/wallet");
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      if (error.response && error.response.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="page-content">
        <div className="container-fluid">
          <div className="row-layout mrb1">
            {dataLoading ? (
              <div className="d-flex justify-content-center">
                <Dataloader />
              </div>
            ) : (
              <>
                <div className="row-hd-txt">
                  <div className="row-hd-txt-left">
                    <img src={wallet} alt="icon" />
                    <h5>{isEditMode ? "Update Credit Wallet Request" : "Request For Credit Wallet"}</h5>
                  </div>
                </div>
                <p className="order-note">
                  <span>Note - </span>Submit a form by selecting the required
                  documents to apply for a credit balance update in your wallet.
                </p>
                {documents.length > 0 ? (
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 credit-required">
                      <div className="col-lg-6">
                        <h6 className="title">Select Document Type*</h6>
                        <select 
                          className="form-control" 
                          value={selectedDocument?.id || ""} 
                          onChange={handleDocumentSelect}
                          disabled={isEditMode}
                        >
                          <option value="">-- Select Document Type --</option>
                          {documents.map((doc) => (
                            <option key={doc.id} value={doc.id}>
                              {doc.name}
                            </option>
                          ))}
                        </select>
                        {isEditMode && (
                          <small className="text-muted d-block mt-1">
                            Document type cannot be changed while editing
                          </small>
                        )}
                      </div>

                      {selectedDocument && selectedDocument.forms && (
                        <>
                          <div className="col-lg-12">
                            <h6 className="title">Required Documents for {selectedDocument.name}</h6>
                          </div>
                          
                          {selectedDocument.forms.map((field, index) => {
                            const trimmedLabel = field.label.trim();
                            return (
                            <div className="col-lg-6" key={index}>
                              <label className="form-label">
                                {field.label}
                                {field.required && <span className="text-danger">*</span>}
                              </label>
                              
                              {field.type === "file" ? (
                                <>
                                  <input
                                    type="file"
                                    className="form-control"
                                    required={field.required && !(isEditMode && formData[trimmedLabel])}
                                    accept="image/*,.pdf"
                                    disabled={uploadingFiles[trimmedLabel]}
                                    onChange={(e) => handleFileChange(trimmedLabel, e.target.files[0])}
                                  />
                                  {uploadingFiles[trimmedLabel] && (
                                    <small className="text-primary">Uploading...</small>
                                  )}
                                  {formData[trimmedLabel] && !uploadingFiles[trimmedLabel] && (
                                    <small className="text-success d-block">✓ File uploaded successfully</small>
                                  )}
                                  {fileUrls[trimmedLabel] && (
                                    <a 
                                      href={fileUrls[trimmedLabel]} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="btn btn-sm btn-link p-0 mt-1"
                                    >
                                      <i className="bi bi-eye me-1"></i>
                                      View Document
                                    </a>
                                  )}
                                </>
                              ) : field.type === "number" ? (
                                <input
                                  type="number"
                                  className="form-control"
                                  placeholder={field.description}
                                  required={field.required && !(isEditMode && formData[trimmedLabel])}
                                  value={formData[trimmedLabel] || ""}
                                  onChange={(e) => handleInputChange(trimmedLabel, e.target.value)}
                                />
                              ) : (
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder={field.description}
                                  required={field.required && !(isEditMode && formData[trimmedLabel])}
                                  value={formData[trimmedLabel] || ""}
                                  onChange={(e) => handleInputChange(trimmedLabel, e.target.value)}
                                />
                              )}

                              {field.description && (
                                <small className="d-block text-muted">
                                  {field.description}
                                </small>
                              )}
                            </div>
                          )})}
                          
                          {isEditMode && (notes || description) && (
                            <>
                              <div className="col-lg-12">
                                <h6 className="title mt-3">Admin Feedback</h6>
                              </div>
                              {notes && (
                                <div className="col-lg-6">
                                  <label className="form-label">Notes</label>
                                  <div className="alert alert-info">
                                    {notes}
                                  </div>
                                </div>
                              )}
                              {description && (
                                <div className="col-lg-6">
                                  <label className="form-label">Description</label>
                                  <div className="alert alert-info">
                                    {description}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                      
                      <div className="col-12 text-end">
                        <button
                          type="submit"
                          className="submit_btn"
                          disabled={loading || !selectedDocument}
                        >
                          <i className="bi bi-floppy"></i>
                          {loading ? (isEditMode ? "Updating..." : "Submitting...") : (isEditMode ? "Update" : "Submit")}
                        </button>
                      </div>
                    </div>
                  </form>
                ) : (
                  <NoData />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
